import { api } from '@/api/http';
import { Button } from '@/components/ui/button';
import type { IClient } from '@/entities/client.entity';
import { cn } from '@/lib/utils';
import {
	ActivityIcon,
	CalendarClockIcon,
	ChevronDownIcon,
	ContainerIcon,
	FileIcon,
	HashIcon,
	LayoutGridIcon,
	UserIcon,
} from 'lucide-react';
import { useState } from 'react';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';
import { toast } from 'sonner';

const formatter = buildFormatter(russianStrings);

interface ClientDeviceMetaProps {
	client: IClient;
	onCopy: (text: string) => void;
	defaultOpen?: boolean;
}

export default function ClientDeviceMeta({ client, onCopy, defaultOpen = false }: ClientDeviceMetaProps) {
	const [open, setOpen] = useState(defaultOpen);

	async function handleDownload() {
		if (!client.fileName) return toast.error('У клиента нет зипа');
		await api.get(`/zip/${client.id}`).then(() => {
			toast.success('ZIP отправлен ⤴️', { position: 'top-center', duration: 2500 });
		});
	}

	return (
		<div className='grid gap-1'>
			<button
				type='button'
				onClick={() => setOpen(v => !v)}
				className='flex items-center gap-1 h-7 text-sm text-muted-foreground hover:text-foreground transition-colors'
			>
				<ChevronDownIcon size={14} className={cn('transition-transform', open && 'rotate-180')} />
				Ещё
			</button>
			{open ? (
				<div className='grid gap-1 pb-1'>
					<button type='button' className='flex gap-1 items-center text-left' onClick={() => onCopy('#' + client.id)}>
						<HashIcon size={14} className='opacity-40 shrink-0' />
						<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.id}</span>
					</button>
					<button type='button' className='flex gap-1 items-center text-left' onClick={() => onCopy(client.profileNickname)}>
						<UserIcon size={14} className='opacity-40 shrink-0' />
						<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.profileNickname}</span>
					</button>
					<button type='button' className='flex gap-1 items-center text-left' onClick={() => onCopy(client.buildId)}>
						<ContainerIcon size={14} className='opacity-40 shrink-0' />
						<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.buildId}</span>
					</button>
					<button type='button' className='flex gap-1 items-center text-left' onClick={() => onCopy(client.sdkVersion)}>
						<LayoutGridIcon size={14} className='opacity-40 shrink-0' />
						<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.sdkVersion}</span>
					</button>
					<div className='flex items-center justify-between gap-2'>
						<div className='grid gap-0.5 min-w-0'>
							<div className='flex gap-1 items-center'>
								<CalendarClockIcon size={14} className='opacity-40 shrink-0' />
								<span className='text-xs text-muted-foreground'>Установлен</span>
								<span className='text-xs text-chart-2'>
									<TimeAgo date={client.createdAt} formatter={formatter} />
								</span>
							</div>
							<div className='flex gap-1 items-center'>
								<ActivityIcon size={14} className='opacity-40 shrink-0' />
								<span className='text-xs text-muted-foreground'>Ласт пинг</span>
								<span className='text-xs text-chart-2'>
									<TimeAgo date={client.onlineAt} formatter={formatter} />
								</span>
							</div>
						</div>
						<Button variant='outline' size='sm' className='shrink-0 h-8' onClick={handleDownload}>
							<FileIcon size={16} />
							Зип
						</Button>
					</div>
				</div>
			) : null}
		</div>
	);
}
